import { useAlert } from 'react-alert';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { ORDER_STATUS_ICON_RESET_INTERVAL } from '../../constants';
import { resetMenu } from '../../reducers/menuSlice';
import {
  AgentInterceptionType,
  EndSessionTransmissionMessage,
  InfoTransmissionMessage,
  TextFrameTransmissionMessage,
  messagingActions,
} from '../../reducers/messagingSlice';
import { selectTRAgentAutoAssignment } from '../../redux/features/config/config.selector';
import { SessionStartReasons } from '../../redux/features/sessionBoundary/sessionBoundary.constants';
import { setSessionStart } from '../../redux/features/sessionBoundary/sessionBoundary.slice';
import { TaskStatuses } from '../../redux/features/taskRouter/taskRouter.constants';
import {
  selectCurrentTask,
  selectTasksById,
} from '../../redux/features/taskRouter/taskRouter.selector';
import { updateTaskStatus } from '../../redux/features/taskRouter/taskRouter.slice';
import logger from '../../utils/logger';
import useAgentInterception from '../agentInterception/useAgentInterception.hooks';
import useTaskRouterHandlers from './taskRouterHandlers.hooks';
import { getAuthRefactorFeatureFlag } from '../../utils/session-storage';

const useTaskRouterMenuHandlers = () => {
  const dispatch = useAppDispatch();
  const alert = useAlert();
  const currentTaskId = useAppSelector(selectCurrentTask);
  const tasksById = useAppSelector(selectTasksById);
  // HITL-1606: exempt from the agent-mode gate. This interception accompanies a
  // MENU LOAD FAILURE — the customer is told and the agent is logged out — so it
  // is the system acting, not the human, and `Shadow` must not suppress it.
  const { sendAgentInterception } = useAgentInterception({
    bypassModeGate: true,
  });
  const { navigateToTaskRouterPage } = useTaskRouterHandlers();
  const isTaskAssignmentMode = useAppSelector(selectTRAgentAutoAssignment);

  const taskRouterMenuLoadFailureCallback = ({
    restaurantCode,
    orderSessionId,
    messageSessionId,
    logout,
  }: {
    restaurantCode: string;
    orderSessionId: string;
    messageSessionId: string;
    logout: Function;
  }) => {
    const message = 'Failed to load menu';

    dispatch(resetMenu());
    if (!getAuthRefactorFeatureFlag()) {
      alert.error(message, { timeout: ORDER_STATUS_ICON_RESET_INTERVAL });
    }

    const endSessionPayload: Partial<EndSessionTransmissionMessage> = {
      data: {
        restaurant_code: restaurantCode,
        session_id: orderSessionId || messageSessionId,
      },
      metadata: message,
    };
    dispatch(
      messagingActions.sendEndSession(
        endSessionPayload as EndSessionTransmissionMessage
      )
    );

    const messagePayload: Partial<TextFrameTransmissionMessage> = {
      // HITL-1612: the one TTS that must still reach the customer outside
      // `Intercept`. This is not an agent action — the menu failed to load, so
      // the customer is told someone is coming and the agent is logged out.
      // Withholding it would leave the customer in silence. Same exemption the
      // `useAgentInterception({ bypassModeGate: true })` below already carries;
      // this adds it to the speech, which A4 did not cover. Local-only key: the
      // socket middleware strips it before the outbound payload.
      bypassModeGate: true,
      data: {
        payload: 'One moment, let me get a team member to help you',
        status: 'final',
        metadata: { intent: '#failedMenuLoad', timeDelta: 1 },
      },
    };
    const infoPayload: Partial<InfoTransmissionMessage> = {
      data: {
        message,
        type: 'METRIC',
        metadata: { event_name: 'intervention_reason', reason: message },
      },
    };
    dispatch(
      messagingActions.sendMessage(
        messagePayload as TextFrameTransmissionMessage
      )
    );
    dispatch(messagingActions.sendInfo(infoPayload as InfoTransmissionMessage));
    sendAgentInterception({
      data: {
        type: AgentInterceptionType.TTS_SENT,
      },
    });

    if (getAuthRefactorFeatureFlag()) {
      logger.error({
        message: 'Menu load failed. Logging out',
        taskId: currentTaskId,
        isTR: true,
      });
      logout(`${message}. Logging out`);
      navigateToTaskRouterPage();
      setTimeout(() => {
        dispatch(
          updateTaskStatus({
            taskId: currentTaskId,
            event: TaskStatuses.loadFail,
          })
        );
      }, ORDER_STATUS_ICON_RESET_INTERVAL);
    } else {
      setTimeout(() => {
        dispatch(
          updateTaskStatus({
            taskId: currentTaskId,
            event: isTaskAssignmentMode
              ? TaskStatuses.loadFail
              : TaskStatuses.canceled,
          })
        );
        navigateToTaskRouterPage();
      }, ORDER_STATUS_ICON_RESET_INTERVAL);
    }
  };

  const taskRouterMenuLoadSuccessCallback = () => {
    logger.info({
      message:
        'In taskRouterMenuLoadSuccessCallback: Updating taskId with status as task_loaded',
      taskId: currentTaskId,
      isTR: true,
    });
    dispatch(
      updateTaskStatus({
        taskId: currentTaskId,
        event: TaskStatuses.loaded,
      })
    );
    // Starts HITL session by accepting a task
    const { session_id: sessionId } = tasksById[currentTaskId];
    dispatch(
      setSessionStart({
        sessionId,
        sessionStartReason: SessionStartReasons.taskAssigned,
      })
    );
  };

  return {
    taskRouterMenuLoadFailureCallback,
    taskRouterMenuLoadSuccessCallback,
  };
};

export default useTaskRouterMenuHandlers;
